import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useRaffle } from '../hooks/useRaffle';
import { useUserEntries } from '../hooks/useUserEntries';
import { useHandover } from '../hooks/useHandover';
import { useDispute } from '../hooks/useDispute';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { confirmWinnerIntent } from '../lib/api';
import { Button, Spinner } from '../components/ui';
import WinnerBanner from '../components/raffle/WinnerBanner';
import HandoverStatus from '../components/raffle/HandoverStatus';
import MessageThread from '../components/raffle/MessageThread';
import ReviewModal, { ReviewPrompt } from '../components/raffle/ReviewModal';
import DisputeModal, { DisputeResponseModal } from '../components/raffle/DisputeModal';
import DisputeStatus from '../components/raffle/DisputeStatus';

/**
 * CompetitionManage Page
 *
 * Post-draw management for winners and sellers:
 * - Pickup address (winner only)
 * - Messaging between parties
 * - Handover confirmation
 * - Reviews
 * - Disputes
 *
 * Only accessible to the winner or seller of a completed competition.
 */
export default function CompetitionManage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();

  // Pass user.uid to reveal pickupAddress for winner
  const { raffle, loading, error } = useRaffle(id, user?.uid);
  const { entries: userEntries } = useUserEntries(id);

  // Only fetch handover when raffle is completed and user is winner/seller
  // This prevents Firestore permission errors since handover rules require winnerId/sellerId match
  const canAccessHandover =
    raffle?.status === 'completed' &&
    (user?.uid === raffle?.winnerId || user?.uid === raffle?.sellerId);

  const {
    handover,
    bothConfirmed,
    winnerIntentConfirmed,
    winnerIntentDeadline,
  } = useHandover(id, user?.uid, {
    enabled: canAccessHandover,
  });
  const {
    dispute,
    submitting: disputeSubmitting,
    raise: raiseDisputeFn,
    respond: respondToDisputeFn,
    canRaiseDispute,
    needsResponse,
    isRaiser,
  } = useDispute(id);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showDisputeResponseModal, setShowDisputeResponseModal] = useState(false);
  const [confirmingIntent, setConfirmingIntent] = useState(false);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error or not found
  if (error || !raffle) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-display font-bold text-cream mb-4">
            Competition Not Found
          </h1>
          <p className="text-cream/60 mb-8">
            This competition may have been removed or doesn't exist.
          </p>
          <Link to="/browse">
            <Button>Browse Competitions</Button>
          </Link>
        </div>
      </div>
    );
  }

  const {
    title,
    photos = [],
    status,
    pickupArea,
    pickupAddress,
    seller,
    sellerId,
    winnerId,
    winnerDisplayName,
  } = raffle;

  const isCompleted = status === 'completed';
  const isWinner = isCompleted && user?.uid === winnerId;
  const isSeller = isCompleted && user?.uid === sellerId;

  // Handler for confirming winner intent
  const handleConfirmIntent = async () => {
    setConfirmingIntent(true);
    try {
      await confirmWinnerIntent({ raffleId: id });
      toast.success('Great! Now message the seller to arrange a pickup time that works for you both.');
    } catch (error) {
      console.error('Error confirming intent:', error);
      if (error.code === 'INTENT_DEADLINE_PASSED') {
        toast.error('The response deadline has passed. A new winner may be selected.');
      } else {
        toast.error('Failed to confirm. Please try again.');
      }
    } finally {
      setConfirmingIntent(false);
    }
  };

  // Access control - only winner or seller can view this page
  if (!isCompleted || (!isWinner && !isSeller)) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-display font-bold text-cream mb-4">
            Access Denied
          </h1>
          <p className="text-cream/60 mb-8">
            {!isCompleted
              ? 'This competition has not ended yet.'
              : 'Only the winner or seller can manage this transaction.'}
          </p>
          <Link to={`/competition/${id}`}>
            <Button>View Competition</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Find user's winning ticket number (if winner)
  const winningTicketNumber =
    isWinner && userEntries.length > 0
      ? userEntries.flatMap((e) => e.ticketNumbers || []).find(() => true)
      : null;

  const otherPartyName = isWinner
    ? seller?.displayName || 'Seller'
    : winnerDisplayName || 'Winner';

  const otherPartyId = isWinner ? sellerId : winnerId;

  return (
    <div className="min-h-screen bg-midnight">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Back link */}
        <Link
          to={`/competition/${id}`}
          className="inline-flex items-center gap-2 text-cream/60 hover:text-primary transition-colors mb-6"
        >
          <BackIcon className="w-4 h-4" />
          Back to competition
        </Link>

        {/* Winner Banner - Full celebration with confetti */}
        {isWinner && (
          <WinnerBanner
            ticketNumber={winningTicketNumber || 1}
            raffleTitle={title}
            showConfetti
          />
        )}

        {/* Seller Success Banner */}
        {isSeller && (
          <div className="bg-gradient-to-br from-success/20 via-success/10 to-transparent border border-success/30 rounded-2xl p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/20 flex items-center justify-center">
              <span className="text-4xl">🎉</span>
            </div>
            <h2 className="text-2xl font-display font-bold text-success mb-2">
              You Have a Winner!
            </h2>
            <p className="text-cream/70">
              Complete the handover to receive your payout
            </p>
          </div>
        )}

        {/* Winner Intent Confirmation - Show if winner hasn't confirmed yet */}
        {isWinner && handover && !winnerIntentConfirmed && (
          <WinnerIntentConfirmation
            deadline={winnerIntentDeadline}
            onConfirm={handleConfirmIntent}
            loading={confirmingIntent}
          />
        )}

        {/* Next Steps Card - Only show full content after winner confirms intent (or for seller) */}
        {(isSeller || winnerIntentConfirmed) && (
        <>
        <div className="mt-8 bg-midnight-light rounded-2xl border border-cream/10 overflow-hidden">
          <div className="px-5 py-4 border-b border-cream/10 bg-midnight-lighter/50">
            <h2 className="text-lg font-display font-bold text-cream flex items-center gap-2">
              <ChecklistIcon className="w-5 h-5 text-primary" />
              Next Steps
            </h2>
          </div>

          <div className="p-5 space-y-4">
            {/* Pickup Address - Winner only */}
            {isWinner && pickupAddress && (
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <LocationIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-primary mb-1">
                      Pickup Location
                    </p>
                    <p className="text-cream font-medium">{pickupAddress}</p>
                    <p className="text-xs text-cream/50 mt-1">
                      {pickupArea}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Handover Status */}
            <HandoverStatus
              handover={handover}
              isWinner={isWinner}
              isSeller={isSeller}
              raffleId={id}
              onConfirmed={() => {
                // Refresh happens automatically via subscription
              }}
            />

            {/* Dispute Status */}
            {dispute && (
              <DisputeStatus
                dispute={dispute}
                isRaiser={isRaiser}
                needsResponse={needsResponse}
                onRespond={() => setShowDisputeResponseModal(true)}
              />
            )}

            {/* Review Prompt - After handover complete */}
            {(bothConfirmed || handover?.status === 'completed') && (
              <ReviewPrompt
                raffleId={id}
                revieweeId={otherPartyId}
                revieweeName={otherPartyName}
                isReviewingAsSeller={isSeller}
                onReviewClick={() => setShowReviewModal(true)}
              />
            )}
          </div>
        </div>

        {/* Messages Card */}
        <div className="mt-6 bg-midnight-light rounded-2xl border border-cream/10 overflow-hidden">
          <div className="px-5 py-4 border-b border-cream/10 bg-midnight-lighter/50">
            <h2 className="text-lg font-display font-bold text-cream flex items-center gap-2">
              <MessageIcon className="w-5 h-5 text-primary" />
              Chat with {otherPartyName}
            </h2>
          </div>

          <div className="p-5">
            <MessageThread
              raffleId={id}
              otherUserId={otherPartyId}
              otherUserName={otherPartyName}
              otherUserAvatar={isWinner ? seller?.photoURL : null}
            />
          </div>
        </div>

        {/* Report Problem Button */}
        {canRaiseDispute && !bothConfirmed && !dispute && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowDisputeModal(true)}
              className="text-cream/40 hover:text-error text-sm transition-colors"
            >
              Having a problem? Report an issue
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        raffleId={id}
        revieweeId={otherPartyId}
        revieweeName={otherPartyName}
        isReviewingAsSeller={isSeller}
      />

      {/* Dispute Modal */}
      <DisputeModal
        isOpen={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
        onSubmit={async (reason, description, evidence) => {
          const result = await raiseDisputeFn(reason, description, evidence);
          if (result.success) {
            setShowDisputeModal(false);
          }
        }}
        isSubmitting={disputeSubmitting}
      />

      {/* Dispute Response Modal */}
      {dispute && (
        <DisputeResponseModal
          isOpen={showDisputeResponseModal}
          onClose={() => setShowDisputeResponseModal(false)}
          onSubmit={async (description, evidence) => {
            const result = await respondToDisputeFn(description, evidence);
            if (result.success) {
              setShowDisputeResponseModal(false);
            }
          }}
          isSubmitting={disputeSubmitting}
          dispute={dispute}
        />
      )}
    </div>
  );
}

/**
 * WinnerIntentConfirmation - Prompt for winner to confirm they want their prize
 * Note: This is about confirming interest/response, NOT committing to collect within 48h
 */
function WinnerIntentConfirmation({ deadline, onConfirm, loading }) {
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(deadline));

  // Update countdown every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeRemaining(deadline));
    }, 60000);
    return () => clearInterval(timer);
  }, [deadline]);

  const isUrgent = timeLeft.hours < 12;
  const isExpired = timeLeft.total <= 0;

  if (isExpired) {
    return (
      <div className="mt-6 bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 shrink-0 rounded-full bg-red-500/20 flex items-center justify-center">
            <ClockIcon className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-400">Response Deadline Passed</h3>
            <p className="text-cream/70 mt-1">
              The response deadline has passed. A new winner may be selected.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-6 ${isUrgent ? 'bg-amber-500/10 border-amber-500/30' : 'bg-primary/10 border-primary/30'} border rounded-2xl p-6`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 shrink-0 rounded-full ${isUrgent ? 'bg-amber-500/20' : 'bg-primary/20'} flex items-center justify-center`}>
          <ClockIcon className={`w-6 h-6 ${isUrgent ? 'text-amber-400' : 'text-primary'}`} />
        </div>
        <div className="flex-1">
          <h3 className={`text-lg font-bold ${isUrgent ? 'text-amber-400' : 'text-primary'}`}>
            Confirm Your Win
          </h3>
          <p className="text-cream/70 mt-1 mb-4">
            Let us know you've seen this and want the item. No response within 48h means we'll pick a new winner.
          </p>

          {/* Countdown */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${isUrgent ? 'bg-amber-500/20' : 'bg-midnight'} mb-4`}>
            <ClockIcon className={`w-4 h-4 ${isUrgent ? 'text-amber-400' : 'text-cream/50'}`} />
            <span className={`text-sm font-mono font-medium ${isUrgent ? 'text-amber-400' : 'text-cream'}`}>
              {timeLeft.hours > 0 && `${timeLeft.hours}h `}
              {timeLeft.minutes}m to respond
            </span>
          </div>

          <div>
            <Button
              onClick={onConfirm}
              loading={loading}
              className={isUrgent ? 'bg-amber-500 hover:bg-amber-600' : ''}
            >
              I Want This!
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function getTimeRemaining(deadline) {
  const total = deadline.getTime() - Date.now();
  const hours = Math.floor(total / (1000 * 60 * 60));
  const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
  return { total, hours, minutes };
}

function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// Icons
function BackIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function LocationIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function MessageIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function ChecklistIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}
